<template>
  <article class="policy-page">
    <header class="policy-header">
      <h1>Integritets- och användarpolicy</h1>
      <p class="policy-updated">Senast uppdaterad: {{ lastUpdated }}</p>
    </header>

    <section>
      <h2>1. Om denna policy</h2>
      <p>
        Den här policyn beskriver hur LiveGBG behandlar dina personuppgifter
        och vilka villkor som gäller när du använder tjänsten. Genom att
        skapa konto eller använda funktioner som favoritmarkering godkänner
        du villkoren nedan.
      </p>
    </section>

    <section>
      <h2>2. Vilken information vi samlar in</h2>
      <p>När du skapar ett konto eller använder tjänsten lagrar vi:</p>
      <ul>
        <li>
          <strong>E-postadress</strong> — för att identifiera kontot och
          eventuellt kommunicera med dig om kontot.
        </li>
        <li>
          <strong>Namn</strong> (om du anger det eller om det skickas via
          Google-inloggning) — visas i admin-vyer för super-admin.
        </li>
        <li>
          <strong>Lösenord</strong> — sparas alltid hashat (bcrypt). Vi kan
          inte se ditt lösenord i klartext.
        </li>
        <li>
          <strong>Profilbild</strong> (avatar) — om du loggar in via Google
          hämtas din avatar-URL från Google.
        </li>
        <li>
          <strong>Inloggningsmetod</strong> — vilka tjänster som är
          kopplade till ditt konto (e-post och/eller Google).
        </li>
        <li>
          <strong>Favoritmarkerade konserter</strong> — vilka events du har
          sparat som favoriter.
        </li>
        <li>
          <strong>Roller och venue</strong> — om du tilldelats arrangörs-
          eller administrationsrättigheter och i så fall för vilken plats.
        </li>
      </ul>
      <p>
        Vi samlar <strong>inte</strong> in IP-adresser, geolokation,
        kontaktlistor, eller annan data utöver det som behövs för
        funktionaliteten ovan.
      </p>
    </section>

    <section>
      <h2>3. Hur vi använder informationen</h2>
      <ul>
        <li>För att låta dig logga in och hålla dig inloggad.</li>
        <li>
          För att visa dina favoritmarkerade konserter under "Mina
          favoriter".
        </li>
        <li>
          För att kunna ge dig rätt behörighet om du är arrangör eller
          administratör.
        </li>
        <li>
          För att visa relevanta data för super-admin (namn, e-post,
          inloggningsmetod) i administrationspanelen.
        </li>
      </ul>
      <p>
        Vi använder inte din data för annonsering, vidareförsäljning eller
        analys utöver vad som krävs för att tjänsten ska fungera.
      </p>
    </section>

    <section>
      <h2>4. Lagring och säkerhet</h2>
      <p>
        All persondata lagras i en databas (MongoDB) som hostas hos
        tredjepartsleverantör. Lösenord lagras endast som bcrypt-hashar.
        Inloggningstokens (JWT) är giltiga i 7 dagar och sparas i din
        webbläsares localStorage.
      </p>
      <p>
        Vi vidtar rimliga tekniska och organisatoriska åtgärder för att
        skydda din information, men ingen lagring eller överföring över
        internet är 100 % säker. Du ansvarar själv för att hålla ditt
        lösenord hemligt.
      </p>
    </section>

    <section>
      <h2>5. Tredjepartstjänster</h2>
      <p>Tjänsten använder följande tredjeparter:</p>
      <ul>
        <li>
          <strong>Google OAuth</strong> — om du väljer att logga in via
          Google delas din e-post, namn och profilbild med oss av Google.
          Google behandlar din inloggning enligt sin egen
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            >integritetspolicy</a
          >.
        </li>
        <li>
          <strong>Cloudinary</strong> — bilder som arrangörer laddar upp
          lagras hos Cloudinary.
        </li>
        <li>
          <strong>Hosting och databas</strong> — applikationen körs på
          molntjänster som hanterar dina data enligt sina egna policyer.
        </li>
      </ul>
    </section>

    <section>
      <h2>6. Cookies och localStorage</h2>
      <p>
        Vi använder inte spårningscookies. Däremot lagrar vi följande i din
        webbläsares localStorage:
      </p>
      <ul>
        <li>Din inloggningstoken (JWT) så du slipper logga in vid varje besök.</li>
        <li>Ditt val av tema (ljust/mörkt läge).</li>
      </ul>
      <p>Du kan när som helst rensa dessa via webbläsarens inställningar.</p>
    </section>

    <section>
      <h2>7. Dina rättigheter</h2>
      <p>Enligt GDPR har du rätt att:</p>
      <ul>
        <li>
          Få ut en kopia av den persondata vi lagrar om dig
          <em>(rätt till tillgång)</em>.
        </li>
        <li>Begära rättelse av felaktiga uppgifter.</li>
        <li>
          Begära att ditt konto och dina personuppgifter raderas
          <em>(rätt att bli glömd)</em>.
        </li>
        <li>Återkalla ditt samtycke när som helst.</li>
        <li>Lämna klagomål till Integritetsskyddsmyndigheten (IMY).</li>
      </ul>
      <p>
        För att utöva dessa rättigheter, kontakta oss via uppgifterna under
        avsnitt 10.
      </p>
    </section>

    <section>
      <h2>8. Användarvillkor</h2>
      <p>När du använder LiveGBG förbinder du dig att:</p>
      <ul>
        <li>
          Lämna korrekt information vid registrering och hålla
          informationen uppdaterad.
        </li>
        <li>
          Inte ladda upp innehåll som är olagligt, kränkande eller bryter
          mot någon annans rättigheter (gäller arrangörer).
        </li>
        <li>Inte missbruka tjänsten eller försöka kringgå
          säkerhetsfunktioner.</li>
        <li>
          Endast använda kontot själv — dela inte ditt lösenord med andra.
        </li>
      </ul>
      <p>
        Vi förbehåller oss rätten att stänga av konton som bryter mot
        villkoren.
      </p>
      <p>
        Tjänsten tillhandahålls "i befintligt skick". Vi gör vårt bästa
        för att hålla informationen aktuell men kan inte garantera att alla
        konsertuppgifter är korrekta — kontrollera alltid med arrangören
        innan du köper biljett eller åker till en konsert.
      </p>
    </section>

    <section>
      <h2>9. Ändringar i policyn</h2>
      <p>
        Den här policyn kan komma att uppdateras. Datumet högst upp visar
        när policyn senast ändrades. Vid större ändringar kommer vi att
        informera dig via e-post eller en notis i tjänsten.
      </p>
    </section>

    <section>
      <h2>10. Kontakt</h2>
      <p>
        Personuppgiftsansvarig är driftansvarig för LiveGBG. Frågor om
        denna policy eller dina personuppgifter besvaras via:
      </p>
      <p>
        <a href="mailto:hello@livegbg.se">hello@livegbg.se</a>
      </p>
    </section>
  </article>
</template>

<script>
export default {
  name: "PolicyPage",
  data() {
    return {
      lastUpdated: "8 maj 2026",
    };
  },
};
</script>

<style scoped>
.policy-page {
  max-width: 760px;
  margin: 0 auto;
  padding: 3rem 2rem 5rem;
  color: var(--color-text);
  line-height: 1.6;
  font-size: 0.95rem;
}

.policy-header {
  margin-bottom: 2.5rem;
  border-bottom: 1px solid var(--color-border);
  padding-bottom: 1.5rem;
}

.policy-header h1 {
  font-family: "Playfair Display", serif;
  font-weight: 900;
  font-size: clamp(1.8rem, 4vw, 2.5rem);
  text-transform: uppercase;
  letter-spacing: -0.01em;
  margin: 0 0 0.5rem;
  line-height: 1.1;
}

.policy-updated {
  color: var(--color-text-secondary);
  font-size: 0.8rem;
  margin: 0;
}

.policy-page section {
  margin-bottom: 2.25rem;
}

.policy-page h2 {
  font-family: "Playfair Display", serif;
  font-size: 1.25rem;
  font-weight: 800;
  margin: 0 0 0.75rem;
  color: var(--color-text);
}

.policy-page p {
  margin: 0 0 0.75rem;
  color: var(--color-text-secondary);
}

.policy-page ul {
  margin: 0 0 0.75rem;
  padding-left: 1.5rem;
}

.policy-page li {
  margin-bottom: 0.4rem;
  color: var(--color-text-secondary);
}

.policy-page a {
  color: var(--color-accent);
  text-decoration: underline;
}

.policy-page a:hover {
  text-decoration: none;
}

.policy-page strong {
  color: var(--color-text);
  font-weight: 600;
}

@media (max-width: 700px) {
  .policy-page {
    padding: 2rem 1.25rem 4rem;
  }
}
</style>
